'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
    Button,
    Card,
    CardContent,
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Label,
    ScrollArea,
    cn,
} from '@repo/shadcn';
import { Plus, PlusCircleIcon, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { RiMedicineBottleFill } from 'react-icons/ri';
import { toast } from 'sonner';

import { MODAL_ROUTE } from '@/constants';
import { useModal } from '@/hooks';

import { ProductDto } from '../../../../features/slice/products/product/product.schema';
import {
    useGetProductsWithVariationsQuery,
    useLazyGetProductSigneVariationsQuery,
} from '../../../../features/slice/products/product/productApiSlice';
import { useCreatePurchasesMutation } from '../../../../features/slice/purchases';
import { useGetSuppliersQuery } from '../../../../features/slice/suppliers';
import { useDebounce } from '../../../../hooks/use-debounce';
import { formatCurrencyInBDT } from '../../../../utils/currency-formater';
import { AutosizeTextarea } from '../../../ui/resizeable-textarea';
import {
    StockItem,
    StockItemSchema,
    SubmissionData,
    SubmissionSchema,
} from './create-stock.schema';
import { ProductAutocomplete } from './product-autocomplete';

function calculateTotals(items: StockItem[]) {
    let totalPurchase = 0;
    let totalWholesale = 0;
    let totalRetail = 0;

    items.forEach((item: StockItem) => {
        totalPurchase += item.purchase_price * item.quantity;
        totalWholesale += item.wholesale_price * item.quantity;
        totalRetail += item.retail_price * item.quantity;
    });

    const profitMargin = totalRetail - totalPurchase;

    return {
        totalPurchase,
        totalWholesale,
        totalRetail,
        profitMargin,
    };
}
interface StockSubmission extends SubmissionData {
    products: StockItem[];
    total_price: number;
    total_paid: number;
}
interface SelectedItem extends StockItem {
    name: string;
}
// export interface ExtendedForm extends StockItem {
//   supplier_id: string
// }
export const CreateStock = () => {
    const { openModal } = useModal();
    const [productSearch, setProductSearch] = useState('');
    const { data: products, isLoading: productLoading } =
        useGetProductsWithVariationsQuery({
            limit: 10,
            page: 1,
            search: String(productSearch).trim() ?? '',
            sort_order: 'asc',
        });
    const [
        createpurchases,
        {
            isLoading: stockSubmitLoading,
            isSuccess: _stockSubmitSuccess,
            error: _stockSubmitError,
        },
    ] = useCreatePurchasesMutation();
    const { data: suppliers, isLoading: supplierLoading } =
        useGetSuppliersQuery({});
    const [getSignleVariation] = useLazyGetProductSigneVariationsQuery();
    const [items, setItems] = useState<SelectedItem[]>([]);
    const [day, setDay] = React.useState('');
    const [month, setMonth] = React.useState('');
    const [year, setYear] = React.useState('');
    const itemForm = useForm<StockItem>({
        resolver: zodResolver(StockItemSchema),
        defaultValues: {
            quantity: 1,
            purchase_price: 0,
            wholesale_price: 0,
            retail_price: 0,
        },
    });

    const { watch, formState } = itemForm;
    const { errors } = formState;

    const expirationDate = watch('expiration_date');

    const submissionForm = useForm<StockSubmission>({
        resolver: zodResolver(SubmissionSchema),
        defaultValues: {
            supplier_id: '',
            note: '',
            products: [],
            total_price: 0,
            total_paid: 0,
        },
    });

    function onAddItem(data: StockItem) {
        if (!expirationDate) {
            itemForm.setError('expiration_date', {
                message: 'Expiration date is required',
            });
            return;
        }
        if (!submissionForm.watch('supplier_id')) {
            submissionForm.setError('supplier_id', {
                message: 'supplier is required',
            });
            return;
        }
        submissionForm.clearErrors();
        // submissionForm.resetField('supplier_id');
        const selectedItem = products.data.find(
            (item: ProductDto) => item.id == data.product_variation_id,
        )?.name;
        setItems([
            ...items,
            {
                ...data,
                name: selectedItem,
            },
        ]);

        itemForm.resetField('product_variation_id');
        itemForm.resetField('purchase_price');
        itemForm.resetField('retail_price');
        itemForm.resetField('wholesale_price');
        itemForm.resetField('quantity');
        itemForm.resetField('expiration_date');
    }
    const debounchSearch = useDebounce((value: string) => {
        setProductSearch(value);
    }, 500);
    const handleProductSearch = (value: string) => {
        debounchSearch(value);
    };

    const handleRemoveItem = (id: string) => {
        setItems(() => items.filter((p) => p.product_variation_id !== id));
    };

    async function onSubmitAll(submissionData: StockSubmission) {
        const stockData: StockSubmission = {
            ...submissionData,
            total_price: submissionData.total_price,
            total_paid: submissionData.total_paid,
            products: items.map((item) => {
                return {
                    expiration_date: item.expiration_date,
                    product_variation_id: item.product_variation_id,
                    purchase_price: item.purchase_price,
                    quantity: item.quantity,
                    retail_price: item.retail_price,
                    wholesale_price: item.wholesale_price,
                };
            }),
        };

        const res = await createpurchases(stockData).unwrap();
        if (res.data) {
            setItems([]);
            submissionForm.reset();
            toast.success('Successfully saved stock', {
                style: {
                    background: 'green',
                    color: 'white',
                },
            });
        } else {
            toast.error('Failed to save!', {
                style: {
                    background: 'red',
                    color: 'white',
                },
            });
        }
    }

    const handleSelectedItemId = async (id: string) => {
        itemForm.setValue('product_variation_id', id);
        const { data } = await getSignleVariation(id);

        if (data?.data) {
            itemForm.setValue(`purchase_price`, data.data.purchase_price);
            itemForm.setValue(`wholesale_price`, data.data.wholesale_price);
            itemForm.setValue(`retail_price`, data.data.retail_price);
        }
    };

    const handleSelectedSupplier = (id: string) => {
        submissionForm.setValue('supplier_id', id);
    };
    const days = Array.from({ length: 31 }, (_, i) => i + 1);
    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];
    const years = Array.from(
        { length: 100 },
        (_, i) => new Date().getFullYear() - i + 10,
    );
    const handleDateChange = (newDay: any, newMonth: any, newYear: any) => {
        setDay(newDay);
        setMonth(newMonth);
        setYear(newYear);

        const newIsoDate = new Date(newYear, newMonth - 1, newDay);
        // filterByDate(newIsoDate);
        itemForm.setValue('expiration_date', newIsoDate);
    };

    return (
        <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
                {/*       <Card className="flex items-center justify-between py-3 h-fit bg-white px-6">
          <div>
            <h1 className="text-xl font-bold">Create New Stock</h1>
            <p className="text-muted-foreground sm">
              Fill out the form to create a new stock.
            </p>
          </div>
          <Link to="/stock">
            <Button variant="secondary" size="sm" className="w-fit gap-2">
              <BiSolidLeftArrow /> Back to Stocks
            </Button>
          </Link>
        </Card> */}

                <Form {...submissionForm}>
                    <form className="space-y-4">
                        <Card className="pt-4">
                            <CardContent className="grid grid-cols-2 gap-3">
                                <FormField
                                    control={submissionForm.control}
                                    name="supplier_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel
                                                className={
                                                    'flex items-center justify-between'
                                                }
                                            >
                                                <span>Supplier</span>
                                                <button
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        openModal(
                                                            MODAL_ROUTE.CREATE_SUPPLIER,
                                                        );
                                                    }}
                                                >
                                                    <PlusCircleIcon />
                                                </button>
                                            </FormLabel>

                                            <ProductAutocomplete
                                                handleProductSearch={
                                                    handleProductSearch
                                                }
                                                isLoading={supplierLoading}
                                                options={
                                                    (suppliers?.data as any[]) ??
                                                    []
                                                }
                                                value={field.value}
                                                handleSelectedItemId={
                                                    handleSelectedSupplier
                                                }
                                                placeholder="Supplier"
                                            />

                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={submissionForm.control}
                                    name="note"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Note</FormLabel>
                                            <FormControl>
                                                <AutosizeTextarea
                                                    {...field}
                                                    placeholder="Add note..."
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>

                        <Card className="relative mx-auto h-[calc(100vh-240px)] w-full overflow-hidden shadow-xl">
                            <CardContent className="pt-2">
                                <ScrollArea className="h-[calc(100vh-460px)] pt-2">
                                    <div className="space-y-3">
                                        {items.map((item) => (
                                            <div
                                                key={item.name}
                                                className="flex items-center justify-between rounded-lg border-[1px] bg-slate-50 p-2"
                                            >
                                                <div className="flex gap-2 space-y-1">
                                                    <div className="flex h-[50px] w-[50px] items-center justify-center rounded-lg border-[1px] bg-blue-50 opacity-90">
                                                        <RiMedicineBottleFill
                                                            size={30}
                                                            className="text-primary"
                                                        />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-[16px] font-medium text-primary">
                                                            {item.name}
                                                        </h3>
                                                        <div className="space-x-4 text-sm text-muted-foreground">
                                                            <span className="font-semibold text-gray-700">
                                                                Qty:{' '}
                                                                {item.quantity}
                                                            </span>
                                                            <span className="font-medium text-green-700">
                                                                Purchase:{' '}
                                                                {formatCurrencyInBDT(
                                                                    item.purchase_price,
                                                                )}
                                                            </span>
                                                            <span className="font-medium text-yellow-700">
                                                                Wholesale:{' '}
                                                                {formatCurrencyInBDT(
                                                                    item.wholesale_price,
                                                                )}
                                                            </span>
                                                            <span className="font-medium text-red-700">
                                                                Retail:{' '}
                                                                {formatCurrencyInBDT(
                                                                    item.retail_price,
                                                                )}{' '}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="bg-red-100 text-destructive hover:bg-destructive/5 hover:text-destructive"
                                                    onClick={() =>
                                                        handleRemoveItem(
                                                            item.product_variation_id,
                                                        )
                                                    }
                                                >
                                                    <Trash2 className="h-5 w-5" />
                                                    <span className="sr-only">
                                                        Remove {item.name}
                                                    </span>
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                    {items.length === 0 && (
                                        <div className="mt-2 rounded-xl border-[1px] border-dashed bg-gray-50">
                                            <p className="py-8 text-center text-muted-foreground">
                                                No items added yet.{' '}
                                            </p>
                                        </div>
                                    )}
                                </ScrollArea>
                            </CardContent>
                            <div className="absolute bottom-0 w-full flex-col border-t-[1px] bg-slate-50 px-6 pb-4">
                                <div className="space-y-4 pt-4">
                                    <div className="ab space-y-1.5">
                                        <Label>
                                            Total ( {items.length}{' '}
                                            {items.length === 1
                                                ? 'item'
                                                : 'items'}
                                            )
                                        </Label>
                                        <div className="text-md flex justify-between font-semibold">
                                            <span className="text-muted-foreground">
                                                Total Purchase
                                            </span>
                                            <div
                                                className="flex items-center gap-2"
                                                onClick={() => {
                                                    submissionForm.setValue(
                                                        'total_price',
                                                        calculateTotals(items)
                                                            .totalPurchase,
                                                    );
                                                }}
                                            >
                                                <span>
                                                    {formatCurrencyInBDT(
                                                        calculateTotals(items)
                                                            .totalPurchase,
                                                    )}
                                                </span>
                                                <FormField
                                                    control={
                                                        submissionForm.control
                                                    }
                                                    name="total_price"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormControl>
                                                                <Input
                                                                    className="w-28 min-w-0 pr-0.5 text-right"
                                                                    type="number"
                                                                    {...field}
                                                                    // onChange={(e) => {
                                                                    //   field.onChange(Number(e.target.value));
                                                                    // }}
                                                                />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className="text-md flex justify-between font-semibold">
                                            <span className="text-muted-foreground">
                                                Total Wholesale
                                            </span>
                                            <span>
                                                {formatCurrencyInBDT(
                                                    calculateTotals(items)
                                                        .totalWholesale,
                                                )}
                                            </span>
                                        </div>
                                        <div className="text-md flex justify-between font-semibold">
                                            <span className="text-muted-foreground">
                                                Total Retail
                                            </span>
                                            <span>
                                                {formatCurrencyInBDT(
                                                    calculateTotals(items)
                                                        .totalRetail,
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex justify-between font-medium">
                                            <span>Profit Margin</span>
                                            <span className="text-green-600">
                                                {formatCurrencyInBDT(
                                                    calculateTotals(items)
                                                        .profitMargin,
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex justify-between font-medium">
                                            <span>Total Paid:</span>
                                            <FormField
                                                control={submissionForm.control}
                                                name="total_paid"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Input
                                                                className="w-28 min-w-0 pr-0.5 text-right"
                                                                type="number"
                                                                {...field}
                                                                onChange={(e) =>
                                                                    field.onChange(
                                                                        Number(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    className="mt-2 w-full"
                                    disabled={items.length === 0}
                                    size="lg"
                                    onClick={submissionForm.handleSubmit(
                                        onSubmitAll,
                                    )}
                                >
                                    {stockSubmitLoading
                                        ? 'Saving...'
                                        : 'Save & Submit All Items'}
                                </Button>
                            </div>
                        </Card>
                    </form>
                </Form>
            </div>
            <div className="space-y-4">
                <Card className="pt-4">
                    <CardContent>
                        <Form {...itemForm}>
                            <form
                                onSubmit={itemForm.handleSubmit(onAddItem)}
                                className="space-y-4"
                            >
                                <FormField
                                    control={itemForm.control}
                                    name="product_variation_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Medicine</FormLabel>
                                            <ProductAutocomplete
                                                handleProductSearch={
                                                    handleProductSearch
                                                }
                                                isLoading={productLoading}
                                                options={products?.data}
                                                value={field.value}
                                                handleSelectedItemId={
                                                    handleSelectedItemId
                                                }
                                                placeholder="Search product"
                                            />
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={itemForm.control}
                                        name="quantity"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Quantity</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        {...field}
                                                        onChange={(e) =>
                                                            field.onChange(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={itemForm.control}
                                        name="expiration_date"
                                        render={() => (
                                            <FormItem>
                                                <FormLabel
                                                    className={cn(
                                                        errors?.expiration_date
                                                            ?.message &&
                                                            'text-red-500',
                                                    )}
                                                >
                                                    Expiry Date{' '}
                                                    {errors?.expiration_date
                                                        ?.message
                                                        ? '(Date is required)'
                                                        : null}
                                                </FormLabel>
                                                <div className="flex space-x-2">
                                                    <select
                                                        className="rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                                        value={day}
                                                        onChange={(e) =>
                                                            handleDateChange(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                                month,
                                                                year,
                                                            )
                                                        }
                                                    >
                                                        {days.map((d) => (
                                                            <option
                                                                key={d}
                                                                value={d}
                                                            >
                                                                {d}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <select
                                                        className="rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                                        value={month}
                                                        onChange={(e) =>
                                                            handleDateChange(
                                                                day,
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                                year,
                                                            )
                                                        }
                                                    >
                                                        {months.map(
                                                            (m, index) => (
                                                                <option
                                                                    key={
                                                                        index +
                                                                        1
                                                                    }
                                                                    value={
                                                                        index +
                                                                        1
                                                                    }
                                                                >
                                                                    {m}
                                                                </option>
                                                            ),
                                                        )}
                                                    </select>

                                                    <select
                                                        className="rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                                                        value={year}
                                                        onChange={(e) =>
                                                            handleDateChange(
                                                                day,
                                                                month,
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    >
                                                        {years.map((y) => (
                                                            <option
                                                                key={y}
                                                                value={y}
                                                            >
                                                                {y}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <FormField
                                        control={itemForm.control}
                                        name="purchase_price"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    Purchase Price (tk)
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        {...field}
                                                        onChange={(e) =>
                                                            field.onChange(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={itemForm.control}
                                        name="wholesale_price"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    Wholesale Price (tk)
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        {...field}
                                                        onChange={(e) =>
                                                            field.onChange(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={itemForm.control}
                                        name="retail_price"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    Retail Price (tk)
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        {...field}
                                                        onChange={(e) =>
                                                            field.onChange(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    variant="secondary"
                                    className="w-full"
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add Item
                                </Button>
                            </form>
                        </Form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
